import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listOrderComments, type OrderCommentRecord } from '../../services/comments';

const ratingColor = (rating: number | null | undefined) => {
  if (rating === null || rating === undefined) return 'neutral';
  if (rating >= 4) return 'success';
  if (rating <= 2) return 'danger';
  return 'warning';
};

const sentimentLabel = (rating: number | null | undefined) => {
  if (rating === null || rating === undefined) return 'Puan yok';
  if (rating >= 4) return 'Pozitif';
  if (rating <= 2) return 'Negatif';
  return 'Nötr';
};

function CommentCard({ comment }: { comment: OrderCommentRecord }) {
  return (
    <div className="card compact">
      <div className="card-header">
        <div>
          <p className="card-title">
            {comment.customerName ?? 'Anonim'} · {sentimentLabel(comment.overallRating)}
          </p>
          <p className="card-subtitle">
            Sipariş {comment.orderId} · {comment.restaurantName ?? 'Restoran bilgisi yok'} ·{' '}
            {new Date(comment.createdAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
          </p>
        </div>
        <span className={`chip ${ratingColor(comment.overallRating)}`}>
          {comment.overallRating ? `${comment.overallRating}/5` : 'Puan yok'}
        </span>
      </div>

      {comment.comment ? <p>{comment.comment}</p> : <p className="helper-text">Yazılı geri bildirim yok.</p>}

      <div className="helper-text chip-row">
        <span>Yemek puanı: {comment.foodRating ?? '—'}</span>
        <span>Teslimat puanı: {comment.deliveryRating ?? '—'}</span>
        <span>Toplam: {comment.orderTotal ? `₺${comment.orderTotal.toFixed(2)}` : '—'}</span>
        <span>Sipariş durumu: {comment.orderStatus ?? '—'}</span>
      </div>
    </div>
  );
}

export default function CommentFeed() {
  const [sentiment, setSentiment] = useState<'all' | 'positive' | 'neutral' | 'negative'>('all');

  const { data: comments = [], isLoading, isFetching, error, isError } = useQuery({
    queryKey: ['feedback', 'comments'],
    queryFn: listOrderComments,
  });

  const filtered = useMemo(() => {
    if (sentiment === 'all') return comments;
    return comments.filter((comment) => {
      const rating = comment.overallRating ?? null;
      if (sentiment === 'positive') return rating !== null && rating >= 4;
      if (sentiment === 'negative') return rating !== null && rating <= 2;
      return rating === null || (rating > 2 && rating < 4);
    });
  }, [comments, sentiment]);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <p className="card-title">Müşteri yorumları</p>
          <p className="card-subtitle">
            {isLoading || isFetching ? 'Geri bildirimler güncelleniyor...' : `${filtered.length} yorum gösteriliyor`}
          </p>
        </div>
        <div className="form-row">
          <select
            value={sentiment}
            onChange={(event) =>
              setSentiment(event.target.value as 'all' | 'positive' | 'neutral' | 'negative')
            }
          >
            <option value="all">Tüm yorumlar</option>
            <option value="positive">Pozitif (≥4)</option>
            <option value="neutral">Nötr (3)</option>
            <option value="negative">Negatif (≤2)</option>
          </select>
        </div>
      </div>

      {isError ? (
        <div className="empty-state">
          <strong>Yorumlar yüklenemedi</strong>
          <p>{error instanceof Error ? error.message : 'order_reviews tablosunun mevcut olduğundan emin olun.'}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <strong>Bu filtre için yorum yok</strong>
          <p>Filtreyi değiştirin veya müşterileri teslimatı puanlamaya teşvik edin.</p>
        </div>
      ) : (
        <div className="card-grid">
          {filtered.map((comment) => (
            <CommentCard key={comment.id} comment={comment} />
          ))}
        </div>
      )}
    </div>
  );
}
